// campaign-update.ts — handleCampaignUpdate caches the new campaign_arr,
// refreshes time/audio widgets, and (GM only) syncs the encounter shell.

import type { WSMessage } from './types';
import { ftUpdateActivityTokenBars } from './activity-token-bars';
import { _ftCamField } from '../shared/campaign-field.js';
import { ftInstallCampaignClockDisplay } from '../shared/campaign-clock-display';

type WSCampaignUpdateMessage = Extract<WSMessage, { type: 'campaign_update' }>;

// Last-seen GM music override, as "<bucket>|<track id>". The initial value is deliberately a
// string no real pair can produce, so the FIRST campaign_update still resolves once; every
// later tick that did not touch these two columns is skipped. See the music block below.
let _ftLastCampaignMx = '<unset>';

// The campaign fields the GM controls draw from (toggles, and the stronghold that enables Push to
// Stronghold). See the rebuild block in handleCampaignUpdate.
const _FT_CAMPAIGN_TOGGLE_FIELDS = [
    'campaign_disable_player_haven_return',
    'campaign_downtime_enabled',
    'hunger_thirst_enabled',
    'waking_weariness_enabled',
    'campaign_ft3d_mode',
    'campaign_stronghold_encounter_id',
];
let _ftLastCampaignToggleSig = '<unset>';
function _ftCampaignToggleSig(arr: Parameters<typeof _ftCamField>[0]): string {
    return _FT_CAMPAIGN_TOGGLE_FIELDS.map((k) => String(_ftCamField(arr, k) ?? '')).join('|');
}

async function handleCampaignUpdate(parsedData: WSCampaignUpdateMessage): Promise<void> {
    if (!_ftCamField(parsedData.campaign_arr, 'campaign_id')) {
        ftDebugLog('campaign', { warn: 'campaign_update missing valid campaign_arr' });
        return;
    }

    const campaignArr = parsedData.campaign_arr;
    // Guarded above.
    const campaignId = _ftCamField(campaignArr, 'campaign_id')!;

    // ONLY cache if this matches the active campaign (prevents stray updates in multi-tab scenarios)
    if (campaignId !== window.FateTide?.campaign?.id) {
        ftDebugLog('campaign', { ignoringOtherCampaign: campaignId });
        return;
    }

    ftApplyCampaignPermissionForViewer(campaignArr);
    ftInstallCampaignClockDisplay(campaignArr);

    // Baseline for the GM-controls rebuild below: the cached campaign the controls were drawn from, so the
    // FIRST push can already correct them (not just record a signature).
    if (_ftLastCampaignToggleSig === '<unset>') {
        const drawnFrom = window.FateTide?.campaignCache?.get(campaignId) as typeof campaignArr | undefined;
        if (drawnFrom) _ftLastCampaignToggleSig = _ftCampaignToggleSig(drawnFrom);
    }

    // CACHE THE FULL OBJECT
    window.FateTide?.campaignCache?.set(campaignId, campaignArr);
    ftDebugLog('campaign', { fullArrCached: campaignId });

    // Clock advanced → reflow any mounted timed-activity progress bars (gathering/reading/training) from
    // the native computed.campaign.campaign_now_seconds (the bar's CSS width-transition
    // glides each step). Lightweight DOM/cache pass — NOT a full ftRerenderPcSheet (which would rebuild
    // every pane on every tick). The action-bar bar + the above-token map bar both refresh here. No-op when
    // nobody is mid-activity / nothing is mounted.
    (window as { ftUpdateActivityBars?: (casterId?: string) => void }).ftUpdateActivityBars?.();
    ftUpdateActivityTokenBars();

    // Immediate UI update. Finish projecting the canonical clock DOM BEFORE notifying secondary
    // consumers: the action-bar halo uses those nodes only to preserve the viewer's 12h/24h display
    // preference. Dispatching first made it render the previous clock value on every push.
    await getCampaignTimeSummary(campaignArr);
    window.dispatchEvent(new CustomEvent('ft:campaign-state-received', {
        detail: { campaignId, campaignArr },
    }));
    // Ambience is scoped by ENCOUNTER: ftSyncViewerAmbience resolves the track from the viewer's OWN
    // encounter (active encounter cache context) for every viewer — GM and players alike — not from the campaign-global
    // campaign_encounter_amb. We route campaign_update through it (rather than playing campaign_encounter_amb
    // directly) both to pick up a day/night sun-state tick and so an unrelated campaign change never
    // re-asserts a stale campaign pointer over a viewer's own encounter. Music stays campaign-global.
    void ftSyncViewerAmbience(campaignArr);
    // Music routes through the resolver now (music-player.ts): campaign_encounter_mx is the GM
    // OVERRIDE layer, above in_combat and the encounter's own bucket. Going through it is what
    // makes "back to default" work — matchCampaignAudio() no-ops on a blank id, so clearing the
    // override used to leave the overridden track playing forever with no way back.
    // Falls back to the old direct call on any page without the player attached.
    //
    // ⚠ Gated on the override ACTUALLY CHANGING. campaign_update also fires on every clock
    // tick, and re-resolving unconditionally meant a DB-hitting request per tick per connected
    // client — load that buys nothing, since the resolver's answer only moves when one of these
    // two columns does. (Combat changes come in on encounter_stats_update, not here.)
    if (typeof window.ftSyncViewerMusic === 'function') {
        const mxNow = String(_ftCamField(campaignArr, 'campaign_encounter_mx_bucket') ?? '')
            + '|' + String(_ftCamField(campaignArr, 'campaign_encounter_mx') ?? '');
        if (mxNow !== _ftLastCampaignMx) {
            _ftLastCampaignMx = mxNow;
            void window.ftSyncViewerMusic();
        }
    } else {
        matchCampaignAudio('audio_player_music_1', _ftCamField(campaignArr, 'campaign_encounter_mx'));
    }

    // Campaign pages receive updates without an encounter context for the sidelog summary.
    if (window.ftGetActiveEncounterId?.()) {
        getEncounterSettingSummary(null, campaignArr);
    }

    // GM controls render the campaign toggles (and their haven <-> downtime interlock) from this cache at
    // BUILD time and only rebuild after their own click. A flag changed anywhere else — another GM tab, a
    // co-GM — left a button showing the old state, or rendered `disabled` so a click sent nothing at all.
    // Rebuild when one of those flags actually moved; clock ticks leave the signature alone.
    const toggleSig = _ftCampaignToggleSig(campaignArr);
    if (_ftLastCampaignToggleSig !== '<unset>' && toggleSig !== _ftLastCampaignToggleSig
        && ftUserIsCampaignGmFromCampaignArr(campaignArr)
        && typeof buildTurnWidgetGMControls === 'function') {
        void Promise.resolve(buildTurnWidgetGMControls()).catch(() => { /* display-only rebuild */ });
    }
    _ftLastCampaignToggleSig = toggleSig;

    // GM-only: campaign.encounter_loaded is the GM's navigation shell. Players stay first-person
    // on their caster-bound encounter; see handleCasterUpdate. Do not rewrite active encounter for non-GMs.
    const newLoadedRaw = _ftCamField(campaignArr, 'encounter_loaded');
    const newLoaded = newLoadedRaw != null ? String(newLoadedRaw).trim() : '';
    // The server validates an explicit staging automation cell before rendering its frame.
    // Shared campaign clock/metadata broadcasts must not navigate that isolated fixture away.
    const requestedCell = new URLSearchParams(window.location.search).get('ft3d_cell');
    const cellFrame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    const cellUrl = cellFrame?.src ? new URL(cellFrame.src, window.location.href) : null;
    const pinnedCell = !!requestedCell
        && cellUrl?.searchParams.get('ft3d_cell') === '1'
        && cellUrl.searchParams.get('encounter_id')?.toUpperCase() === requestedCell.toUpperCase();
    if (
        !pinnedCell && ftUserIsCampaignGmFromCampaignArr(campaignArr)
        && newLoaded
        && typeof checkIfValidUUID === 'function'
        && checkIfValidUUID(newLoaded)
    ) {
        const prev = String(window.ftGetActiveEncounterId?.() || '').trim();
        if (newLoaded !== prev) {
            window.ftSetActiveEncounterId?.(newLoaded, 'campaign-update');
        }
    }

    if (typeof window.applyFtMapLoadedEncounterHighlight === 'function') {
        if (ftUserIsCampaignGmFromCampaignArr(campaignArr)) {
            window.applyFtMapLoadedEncounterHighlight(
                (newLoaded && typeof checkIfValidUUID === 'function' && checkIfValidUUID(newLoaded))
                    ? newLoaded
                    : undefined,
            );
        } else {
            window.applyFtMapLoadedEncounterHighlight();
        }
    }

    ftDebugLog('campaign', { encounterSummaryRefreshed: campaignId });
}



export { handleCampaignUpdate };
